/**
 * Ownership rules governing whether a base dataset may be exported from the
 * currently loaded session.
 *
 * A base dataset ID is derived client-side as `{sessionId}_{messageIndex}`. Exporting
 * from a session the current user does not own would therefore derive an ID from the
 * owner's session ID, producing the same base dataset ID that the owner (or any other
 * viewer of the same shared session) would generate for the same message. Cosmos DB
 * partitions export manifests by user alias, so such a duplicate is not rejected on
 * write. Cloning the session first assigns a brand new session ID, which keeps the
 * derived base dataset ID unique to the cloning user.
 */

export const EXPORT_DISABLED_NOT_SESSION_OWNER =
    'Please clone this session to export your own dataset';

export interface SessionOwnershipState {
    /** Session ID currently loaded. Empty string when no session has been persisted yet. */
    sessionId: string;
    /** Owner of the loaded session, as resolved when the session was loaded. */
    sessionOwner: string;
    /** Explicit flag set when a loaded session resolved to a different owner. */
    isViewingOthersSession: boolean;
    /** Current signed-in user, in the same form stored as the session owner. */
    currentUserName: string;
}

/**
 * Returns true only when the current user positively owns the loaded session.
 *
 * This deliberately fails closed: if ownership cannot be established (for example the
 * session owner has not been resolved yet), exporting is not permitted. A session that
 * has not been persisted yet has no owner to compare against and belongs to the current
 * user by construction.
 */
export function canExportFromSession(state: SessionOwnershipState): boolean {
    if (state.isViewingOthersSession) {
        return false;
    }

    // A chat that has not yet been persisted is implicitly owned by the current user.
    if (!state.sessionId) {
        return true;
    }

    return !!state.currentUserName && state.sessionOwner === state.currentUserName;
}
